import { cva, type VariantProps } from 'class-variance-authority';

/** Shared chrome for the stacked `two-line` field. */
const twoLineTriggerClasses = [
  // Background and border colors - same as default
  'bg-surface-field',
  'text-body',
  // Field-scoped border color token (see `default`).
  'border border-border-field',
  // Hover and focus read field-scoped tokens (see `default`).
  'hover:border-border-field-hover',
  'focus-visible:border-border-field-focus',
  'focus-visible:ring-field-focus',
  'focus-visible:ring-field-focus-width',
  'focus:border-border-field-open',
  'focus:ring-field-open',
  'focus:ring-field-open-width',
  'data-[state=open]:border-border-field-open',
  'data-[state=open]:ring-field-open',
  'data-[state=open]:ring-field-open-width',
  // Disabled colors; the border is pinned so hover cannot show
  'disabled:bg-neutral-100',
  'disabled:border-border-field',
  'disabled:text-subtext',
  // Two-line specific: taller trigger so the label and the
  // value/placeholder both sit comfortably inside a single
  // 56px box (per design spec). `gap-2` spaces the content
  // column from the chevron. The shadcn base applies
  // `[&>span]:line-clamp-1`, which targeted the old single
  // SelectValue span; with the stacked layout the truncate
  // moves to the inner subtext span itself.
  'h-14',
  'pt-2 pb-1.5',
  'gap-2',
] as const;

/**
 * HEN Select variants using HEN Design System tokens.
 * All styling references design tokens defined in tailwind.config.ts.
 */
export const selectTriggerVariants = cva(
  [
    // Base styles - structural styles only
    'flex w-full items-center justify-between',
    'h-11',
    'text-b5',
    'font-body font-normal',
    // Falls back to `rounded-sm` when `--radius-field` is unset; a tenant can opt
    // in to a smaller field radius. `elevated` keeps its own `rounded-xl`.
    // A chosen `rounded` is merged afterwards and replaces either class.
    'rounded-field',
    'px-3 pt-2 pb-1.5',
    'transition-colors duration-200',
    // Focus styles - visible focus ring per WCAG 2.1 AA
    'focus:outline-none',
    'focus-visible:outline-none',
    'focus-visible:ring-0',
    'focus-visible:ring-offset-0',
    // Disabled styles
    'disabled:cursor-not-allowed',
    'disabled:opacity-60',
    '[&>span]:line-clamp-1',
  ],
  {
    variants: {
      variant: {
        default: [
          // Background and border colors
          'bg-surface-field',
          'text-body',
          // Resolves to `border-neutral-200` unless `--border-field` is set;
          // A tenant can go borderless. 1px width stays (no layout shift).
          'border border-border-field',
          // Hover and focus read field-scoped tokens; hover follows the resting
          // border, so a borderless field stays borderless on hover.
          'hover:border-border-field-hover',
          'focus-visible:border-border-field-focus',
          'focus-visible:ring-field-focus',
          'focus-visible:ring-field-focus-width',
          // Focus moves into the menu while open and returns without
          // :focus-visible after a pointer pick; a themed outline stays on.
          'focus:border-border-field-open',
          'focus:ring-field-open',
          'focus:ring-field-open-width',
          'data-[state=open]:border-border-field-open',
          'data-[state=open]:ring-field-open',
          'data-[state=open]:ring-field-open-width',
          // Disabled colors
          'disabled:bg-neutral-100',
          'disabled:border-neutral-200',
          'disabled:text-subtext',
        ],
        elevated: [
          'bg-surface-field',
          'text-body',
          'rounded-xl',
          'border border-neutral-100',
          'shadow-xs',
          'hover:border-border-field-hover-elevated',
          'focus-visible:border-border-field-focus',
          'focus-visible:ring-field-focus',
          'focus-visible:ring-field-focus-width',
          // Open or pointer-focused: keep the themed outline on, falling back
          // to the elevated resting border when nothing is set.
          'focus:border-border-field-open-elevated',
          'focus:ring-field-open',
          'focus:ring-field-open-width',
          'data-[state=open]:border-border-field-open-elevated',
          'data-[state=open]:ring-field-open',
          'data-[state=open]:ring-field-open-width',
          'disabled:bg-neutral-100',
          'disabled:border-neutral-200',
          'disabled:text-subtext',
        ],
        'two-line': [...twoLineTriggerClasses],
        // Same single-line field as `default`. MultiSelect draws the label
        // and the selection count on that one line. `gap-2` matches two-line
        // so the chevron stays clear of the label.
        'one-line': [
          'bg-surface-field',
          'text-body',
          'gap-2',
          'border border-border-field',
          'hover:border-border-field-hover',
          'focus-visible:border-border-field-focus',
          'focus-visible:ring-field-focus',
          'focus-visible:ring-field-focus-width',
          'focus:border-border-field-open',
          'focus:ring-field-open',
          'focus:ring-field-open-width',
          'data-[state=open]:border-border-field-open',
          'data-[state=open]:ring-field-open',
          'data-[state=open]:ring-field-open-width',
          'disabled:bg-neutral-100',
          'disabled:border-neutral-200',
          'disabled:text-subtext',
        ],
      },
      error: {
        true: [
          // Error border
          'border-error-500',
          'text-error-500',
          // Error hover and focus styles
          'hover:border-error-500',
          'focus-visible:border-error-500',
          'focus-visible:ring-2',
          'focus-visible:ring-error-100',
          'focus:border-error-500',
          'focus:ring-transparent',
          'data-[state=open]:border-error-500',
          'data-[state=open]:ring-transparent',
        ],
        false: '',
      },
      hasValue: {
        true: 'text-body',
        false: 'text-subtext',
      },
      /**
       * Fill of the button once a value is chosen. `default` adds nothing.
       * `primary` and `secondary` reuse Button's fill utilities, so a tenant's
       * button tokens apply here. Applied only together with `hasValue`.
       */
      selectedStateTheme: {
        default: '',
        primary: '',
        secondary: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      error: false,
      hasValue: false,
      selectedStateTheme: 'default',
    },
    compoundVariants: [
      {
        error: true,
        hasValue: true,
        className: 'text-error-500',
      },
      {
        hasValue: true,
        error: false,
        selectedStateTheme: 'primary',
        className: [
          'bg-button-primary text-white',
          'hover:bg-button-primary-hover',
          'active:bg-button-primary-active',
        ],
      },
      {
        hasValue: true,
        error: false,
        selectedStateTheme: 'secondary',
        className: [
          'bg-button-secondary text-white',
          'hover:bg-button-secondary-hover',
          'active:bg-button-secondary-active',
        ],
      },
      {
        variant: 'two-line',
        error: true,
        className: 'disabled:border-error-500',
      },
    ],
  },
);

export type SelectTriggerVariantProps = VariantProps<
  typeof selectTriggerVariants
>;

export const SELECTED_STATE_THEMES = [
  'default',
  'primary',
  'secondary',
] as const;

export type SelectedStateTheme = (typeof SELECTED_STATE_THEMES)[number];

/**
 * A chosen value paints the button only for `primary` and `secondary`.
 * `default`, an empty authoring value, and an invalid field leave it alone.
 */
export const isFilledSelectedState = (
  theme: SelectedStateTheme | '' | null | undefined,
  hasValue: boolean,
  isInvalid = false,
): boolean =>
  hasValue && !isInvalid && (theme === 'primary' || theme === 'secondary');

/** Stacked label-and-value layout. */
export const isTwoLineSelectVariant = (
  variant: SelectTriggerVariantProps['variant'],
): boolean => variant === 'two-line';

/** Label and chosen answer on one line inside the button. */
export const isOneLineSelectVariant = (
  variant: SelectTriggerVariantProps['variant'],
): boolean => variant === 'one-line';

/** Corner radii an author can pick. Unset keeps the style's own corners. */
export const SELECT_ROUNDED = [
  'none',
  'xxs',
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  'max',
] as const;

export type SelectRounded = (typeof SELECT_ROUNDED)[number];

const selectRoundedClassName = {
  none: 'rounded-none',
  xxs: 'rounded-xxs',
  xs: 'rounded-xs',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  max: 'rounded-max',
} as const satisfies Record<SelectRounded, string>;

/**
 * Class for a chosen radius. Applied after the style classes so it replaces
 * `rounded-field` and the `rounded-xl` baked into `elevated`.
 */
export const selectRoundedClass = (
  rounded: string | null | undefined,
): string | undefined => {
  if (!rounded || !(SELECT_ROUNDED as readonly string[]).includes(rounded)) {
    return undefined;
  }
  return selectRoundedClassName[rounded as SelectRounded];
};
